import { createFileRoute, redirect } from "@tanstack/react-router";
import { PublishPage } from "#/components/publish/PublishPage";

// Posts (#592, was `/publish`). D-590-1: the v2 IA treats Posts as a
// first-class surface for every publication (doc-08 §1.4 "publish comes
// first"), so the old `canPublish` dogfood gate is dropped here — it becomes
// a rollout feature flag on the epic (`#602`) if still needed, not a
// permanent per-workspace nav/route condition.
export const Route = createFileRoute("/dashboard/w/$workspaceId/posts/")({
  // `?new=true` is onboarding's legacy "Write it" hand-off shape (#601). It
  // used to open a blank composer inline (D-596-5 predates #728's dedicated
  // `/posts/new` route); now it's a deterministic, loop-free redirect there
  // instead — `/posts/new` parses no `new` search param of its own, so there
  // is exactly one hop. `OnboardingFirstPost` navigates straight to
  // `/posts/new` now, so this only exists for legacy bookmarks/links.
  //
  // Matched against the sentinel rather than tested for truthiness: opening a
  // blank composer replaces whatever the user was looking at, so any `new=…`
  // value the app never emits (`?new=no`, a stray array) must be ignored, not
  // obeyed — same rule `_home.approve.$hash`'s `just_approved` follows.
  validateSearch: (search: Record<string, unknown>): { new?: true } =>
    search.new === true ||
    search.new === "true" ||
    search.new === 1 ||
    search.new === "1"
      ? { new: true }
      : {},
  beforeLoad: ({ params, search }) => {
    if (search.new) {
      throw redirect({
        to: "/dashboard/w/$workspaceId/posts/new",
        params: { workspaceId: params.workspaceId },
        // Replace, never push: the legacy `?new=true` entry must not stay in
        // the history stack for Back to land on and be bounced forward off
        // again — the property the pre-#728 `navigate({ search: {}, replace:
        // true })` carried explicitly. The router already forces `replace` on
        // a redirect thrown from `beforeLoad`; stating it here pins the intent
        // against that internal changing, and lets a test assert it.
        replace: true,
      });
    }
  },
  component: PostsIndexRoute,
});

function PostsIndexRoute() {
  const { workspaceId } = Route.useParams();
  const navigate = Route.useNavigate();
  // Remount the whole page (and its once-seeded editor/settings state) when
  // the workspace param changes without a route unmount, so we never show
  // one workspace's blog settings or posts under another.
  return (
    <PublishPage
      key={workspaceId}
      workspaceId={workspaceId}
      // Editing is a real navigation now (design §3), not a local state
      // toggle — so an open editor has a URL that survives a refresh.
      onEdit={(postId) =>
        void navigate({
          to: "/dashboard/w/$workspaceId/posts/$postId",
          params: { workspaceId, postId },
        })
      }
    />
  );
}
